import dayjs from "dayjs";
import isLeapYear from "dayjs/plugin/isLeapYear";
import { type ChangeEvent, useState } from "react";
dayjs.extend(isLeapYear);

import { InputText } from "@/components/base/input/input-text";
import { Select } from "@/components/base/input/select";
import { LabelledBy } from "@/components/base/label";
import { HorizontalContainer } from "@/components/layout/horizontal-container";
import { VerticalContainer } from "@/components/layout/vertical-container";
import { VerticalSpacer } from "@/components/layout/vertical-spacer";
import { LocalizedJoi } from "@/utils/localized-joi";
import type { FormInputBaseProps } from "./form-input.model";

const yearRegex = /^\d{4}$/;
interface DateInputProps extends FormInputBaseProps {
  externalValidationError?: string;
  onChange: (date: string | null, e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  value: string;
}

export const DateInput = ({ externalValidationError, id, onBlur, onChange, showLabel, value }: DateInputProps) => {
  // setter dag, måned og år hvis datoen er gyldig
  const [day, setDay] = useState<string>(value && dayjs(value).isValid() ? dayjs(value).format("DD") : "");
  const [month, setMonth] = useState<string>(value && dayjs(value).isValid() ? dayjs(value).format("MM") : "");
  const [year, setYear] = useState<string>(value && dayjs(value).isValid() ? dayjs(value).format("YYYY") : "");

  //bruker 2024 som default for år ettersom det er et skuddår
  const daysInMonth = dayjs()
    .set("date", day ? +day : 0)
    .set("month", month ? +month - 1 : 0)
    .set("year", year && year.length === 4 ? +year : 2024)
    .daysInMonth();

  // setter verdier i select-menyer, med leading zero for tall under 10
  const days = Array.from({ length: daysInMonth }, (_, i) => (i < 9 ? `0${i + 1}` : `${i + 1}`));
  const months = Array.from({ length: 12 }, (_, i) => (i < 9 ? `0${i + 1}` : `${i + 1}`));

  // oppdaterer fødselsdato
  const updateBirthDate = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    selectedDay: string,
    selectedMonth: string,
    selectedYear: string,
  ) => {
    // oppdaterer først når alle data er fylt ut korrekt
    if (selectedDay && selectedMonth && selectedYear) {
      let newDate = dayjs(`${selectedYear}-${selectedMonth}-${selectedDay}`).format("YYYY-MM-DD");

      const selectedDaysInMonth = dayjs()
        .set("date", selectedDay ? +selectedDay : 0)
        .set("month", selectedMonth ? +selectedMonth - 1 : 0)
        .set("year", selectedYear ? +selectedYear : 2000)
        .daysInMonth();

      if (+selectedDay > selectedDaysInMonth) {
        newDate = ""; // nullstiller datoen hvis valgt dag er større enn antall dager i februar
        setDay("");
      }

      onChange(newDate, e); // formaterer her, for å få korrekt dato ved skuddår og korte/lange måneder
    } else {
      onChange("", e); // nullstiller datoen hvis den ikke er korrekt utfylt
    }
  };

  const defaultLabelId = "input-birthdate";

  return (
    <VerticalContainer width="full">
      <LabelledBy hidden={!showLabel} id={id ?? defaultLabelId} value="Fødselsdato" />

      <HorizontalContainer noWrap={true}>
        <Select
          id={`input-birthday${id ? `-${id}` : ""}`}
          labelledBy={id ?? defaultLabelId}
          name="Dag"
          onBlur={() => {
            if (onBlur && day && month && year) {
              onBlur();
            }
          }}
          onChange={(e: ChangeEvent<HTMLSelectElement>) => {
            setDay(e.target.value);
            updateBirthDate(e, e.target.value, month, year);
          }}
          options={days}
          placeholder="Dag"
          required={true}
          value={day}
          autoComplete="bday-day"
        />
        <VerticalSpacer size={"xx-small"} />

        <Select
          id={`input-birthmonth${id ? `-${id}` : ""}`}
          labelledBy={id ?? defaultLabelId}
          name="Måned"
          onBlur={() => {
            if (onBlur && day && month && year) {
              onBlur();
            }
          }}
          onChange={(e: ChangeEvent<HTMLSelectElement>) => {
            setMonth(e.target.value);
            updateBirthDate(e, day, e.target.value, year);
          }}
          options={months}
          placeholder="Måned"
          required={true}
          value={month}
          autoComplete="bday-month"
        />

        <VerticalSpacer size={"xx-small"} />

        <InputText
          externalValidationError={externalValidationError}
          id={`input-birthyear${id ? `-${id}` : ""}`}
          labelledBy={id ?? defaultLabelId}
          name="År"
          onBlur={onBlur}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            // begrens input til 4 siffer
            if (e.target.value.length <= 4) {
              setYear(e.target.value);
              if (e.target.value.length === 4) {
                updateBirthDate(e, day, month, e.target.value);
              }
            }
          }}
          placeholder="År"
          required={true}
          size="small"
          validationSchema={LocalizedJoi.string().label("År").length(4).regex(yearRegex)}
          value={year}
          autoComplete="bday-year"
        />
      </HorizontalContainer>
    </VerticalContainer>
  );
};
